import { Analytics, getOfficeAnalyticsContext } from './analytics';
import { PARTIAL_OFFER_STATUS } from './constants';
import { PaymentPurpose } from './paymentPurpose';

export const PAYMENT_PROMPT_CHECKPOINTS = [
  'instabook_toggle',
  'instabook_book_now',
  'offer_details_view',
  'post_page_load',
  'post_submit',
  'home_location_select',
] as const;

export type PaymentPromptCheckpoint = typeof PAYMENT_PROMPT_CHECKPOINTS[number];
export type PaymentPromptAction = 'cancel' | 'proceed';
export type PaymentPromptRemediation = 'add' | 'revalidate' | 'corporate_managed' | 'retry';
export type PaymentPromptSourcePage =
  | 'find_a_temp_v2'
  | 'post_job_legacy'
  | 'dentist_home'
  | 'custom_offers'
  | 'custom_offer_details'
  | 'job_posted'
  | 'location_details'
  | 'payment_info';
export type RevenueCardCheckpointOrigin = PaymentPromptCheckpoint | 'direct_payment_info';

export type PaymentPromptInteractionGuard = {
  begin: () => boolean;
  dismiss: () => boolean;
};

/** Exact-once guard for custom prompt surfaces that cannot use the shared modal. */
export const createPaymentPromptInteractionGuard = (): PaymentPromptInteractionGuard => {
  let shown = false;
  let dismissed = false;
  return {
    begin: () => {
      if (shown && !dismissed) return false;
      shown = true;
      dismissed = false;
      return true;
    },
    dismiss: () => {
      if (!shown || dismissed) return false;
      dismissed = true;
      return true;
    },
  };
};

const INTENDED_CHECKPOINTS = new Set<PaymentPromptCheckpoint>([
  'instabook_toggle',
  'instabook_book_now',
  'offer_details_view',
]);

export const getPaymentCheckpointType = (
  checkpoint: PaymentPromptCheckpoint,
): 'intended' | 'unintended' => (
  INTENDED_CHECKPOINTS.has(checkpoint) ? 'intended' : 'unintended'
);

/** Expired/removed offers are deliberately viewable without the payment wall. */
export const isOfferDetailsPaymentPromptEligible = (status: unknown): boolean => {
  const normalized = Number(status);
  return normalized !== PARTIAL_OFFER_STATUS.EXPIRED
    && normalized !== PARTIAL_OFFER_STATUS.REMOVED;
};

export const shouldShowScentPolicyInterruption = (
  skipScentCheck: boolean,
  scentFreePolicy: unknown,
): boolean => !skipScentCheck && !scentFreePolicy;

export const shouldShowSoftwareRatingInterruption = (
  softwareRating: unknown,
): boolean => !softwareRating;

export const isInstabookToggleDisabled = (
  selectedLocationPaymentReady: boolean,
): boolean => !selectedLocationPaymentReady;

export const shouldOpenInstabookPaymentPrompt = (
  checked: boolean,
  selectedLocationPaymentReady: boolean,
  revenuePaymentRequired: boolean,
): boolean => checked && selectedLocationPaymentReady && revenuePaymentRequired;

type PromptEventInput = {
  checkpoint: PaymentPromptCheckpoint;
  sourcePage: PaymentPromptSourcePage;
  locationId?: number | string | null;
  jobId?: number | string | null;
  offerId?: number | string | null;
  paymentRemediation?: PaymentPromptRemediation;
};

const buildPromptPayload = ({
  checkpoint,
  sourcePage,
  locationId,
  jobId,
  offerId,
  paymentRemediation,
}: PromptEventInput) => ({
  ...getOfficeAnalyticsContext(locationId),
  checkpoint,
  checkpoint_type: getPaymentCheckpointType(checkpoint),
  source_page: sourcePage,
  ...(jobId != null ? { job_id: String(jobId) } : {}),
  ...(offerId != null ? { offer_id: String(offerId) } : {}),
  ...(paymentRemediation ? { payment_remediation: paymentRemediation } : {}),
});

export const trackPaymentPromptShown = (input: PromptEventInput) => {
  Analytics.track('payment_required_modal_shown', buildPromptPayload(input));
};

export const trackPaymentPromptDismissed = (
  input: PromptEventInput & { action: PaymentPromptAction },
) => {
  const { action, ...prompt } = input;
  Analytics.track('payment_required_modal_dismissed', {
    ...buildPromptPayload(prompt),
    action,
  });
};

export const trackRevenueCardAddResult = ({
  result,
  sourcePage,
  checkpointOrigin,
  locationId,
  failureStage,
  paymentPurpose,
  jobId,
  offerId,
  paymentRemediation,
}: {
  result: 'succeeded' | 'failed';
  sourcePage: PaymentPromptSourcePage;
  checkpointOrigin: RevenueCardCheckpointOrigin;
  locationId?: number | string | null;
  failureStage?: 'setup_intent' | 'stripe_confirmation' | 'payment_update';
  paymentPurpose?: PaymentPurpose;
  jobId?: number | string | null;
  offerId?: number | string | null;
  paymentRemediation: Extract<PaymentPromptRemediation, 'add' | 'revalidate'>;
}) => {
  Analytics.track(
    result === 'succeeded' ? 'revenue_card_add_succeeded' : 'revenue_card_add_failed',
    {
      ...getOfficeAnalyticsContext(locationId),
      source_page: sourcePage,
      checkpoint_origin: checkpointOrigin,
      ...(paymentPurpose ? { payment_purpose: paymentPurpose } : {}),
      ...(jobId != null ? { job_id: String(jobId) } : {}),
      ...(offerId != null ? { offer_id: String(offerId) } : {}),
      payment_remediation: paymentRemediation,
      ...(result === 'failed' && failureStage ? { failure_stage: failureStage } : {}),
    },
  );
};
